import {groupBy, groupByUniqueKey, intersect, mapObject} from '@benchling/js-utils';
import produce from 'immer';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ProcedureType, DilutionAssignmentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {MAX_ENTRY_NAME_LENGTH} from 'notebook/entry-templates/clampEntryName';
import {
  createEdgeId,
  createHandleIdForMaterial,
  createHandleIdForMethod,
  getMaterialCidFromHandle,
} from 'procedures/design/handleIdentifierUtils';
import {
  ADD_NODE_BUTTON_WIDTH,
  COMMON_NODE_WIDTH,
  DASHED_LINE_LENGTH,
  FLOWCHART_PADDING_X,
  FLOWCHART_PADDING_Y,
  getMethodNamePlaceholder,
  MAX_PROCEDURE_FLOWCHART_DEFAULT_ZOOM,
  MIN_PROCEDURE_FLOWCHART_DEFAULT_ZOOM,
  NODE_NAME_BOX_HEIGHT,
  PROCEDURE_HEADER_HEIGHT,
  SUB_ELEMENT_HEIGHT,
  SUB_TITLE_HEIGHT,
} from 'procedures/design/procedureMethodNodeCommonConsts';
import {makeEmptyStepGroups} from 'procedures/design/step-config/stepManagementUtils';
import {
  CanvasGridCoordinates,
  DilutionPlan,
  getMethodComponentCid,
  getMethodComponentName,
  MaterialEdge,
  MaterialFlow,
  Method,
  MethodEdge,
  MethodNode,
  ProcedureEdge,
  ProcedureEdgeType,
  ProcedureFlowchart,
  ProcedureNodeType,
  ProcedureStepConfig,
} from 'procedures/ProcedureDomainTypes';
import {Optional} from 'util/Types';

import {DropZoneType, MethodDropZone} from './methodDragOverlay/ProcedureMethodDragOverlayTypes';

// Keep in sync with BE functions in /src/benchling/procedures/domain/clone_flowchart_helpers.py
// Prefixes are added b/c enzyme CSS selectors don't support HTML ids that start with a number
// Only exported for tests
export function generateNewParameterCid(): string {
  return `param-${uuidv4()}`;
}

export function generateNewDataParameterCid(): string {
  return `data-param-${uuidv4()}`;
}

export function generateNewMaterialInputCid(): string {
  return `matin-${uuidv4()}`;
}

export function generateNewMaterialOutputCid(): string {
  return `matout-${uuidv4()}`;
}

export function generateNewEquipmentCid(): string {
  return `equip-${uuidv4()}`;
}

export function generateNewMethodCid(): string {
  return `method-${uuidv4()}`;
}

export function generateNewStepCid(): string {
  return `worksheet-step-${uuidv4()}`;
}

/**
 * This function generates a new step with new cid, removes the document id and document version id.
 * @param step step object to copy
 * @param name name of the copied step
 */
export function duplicateStep(step: ProcedureStepConfig, name: string): ProcedureStepConfig {
  return produce(step, (draftStep) => {
    draftStep.id = null;
    draftStep.cid = generateNewStepCid();
    draftStep.documentVersionUpdate = null;
    draftStep.documentId = null;
    draftStep.fromId = null;
    draftStep.name = name;
  });
}

/**
 * This function is for duplicating methods. It generates a new cid for the newly cloned node.
 * @param canvasNode node to copy
 * @param newNodeCid client ID to install into the new node
 * @param newName optionally, provide a name for this node as it's created
 * @return Object {newMethodNode, cloneStepCidToDocumentVersionIdMap} returns copied node with new cid and a
 * map to provide to backend to indicate which documents to copy when duplicating steps.
 *
 * @package Exported only for testing
 */
export function duplicateNode(
  canvasNode: MethodNode,
  newNodeCid: string,
  removeTestDefinitionsAndDilutionPlan: boolean,
  newName?: string
): {
  newMethodNode: MethodNode;
  cloneStepCidToDocumentVersionIdMap: Record<string, string>;
} {
  const cloneStepCidToDocumentVersionIdMap: Record<string, string> = {};
  const newMethodNode = produce(canvasNode, (draftNode) => {
    // Note: provenanceId is intentionally preserved (tracks method lineage across flowcharts).
    // nodeId must be a fresh UUID because this is a new, distinct node in the same flowchart —
    // two nodes with the same nodeId in one flowchart break ordering assertions.
    draftNode.nodeId = uuidv4();
    draftNode.cid = newNodeCid;
    draftNode.id = null;
    draftNode.sourceMethodId = canvasNode.id; // Link back to method we are duplicating
    if (newName != null) {
      draftNode.name = newName;
    }

    for (const obj of draftNode.dataParameterSettings) {
      const cid = generateNewDataParameterCid();
      obj.dataParameterVersion.clonedFromId = obj.dataParameterVersion.id;
      obj.dataParameterVersion.id = null;
      obj.dataParameterVersion.cid = cid;
      // Resetting the schema ID to ensure a new schema gets made to avoid reuse especially in new flowcharts
      obj.dataParameterVersion.procedureDataParameterId = null;

      for (const [i, value] of obj.values.entries()) {
        value.id = null;
        value.cid = `${cid}-planned-value-${i}`;
      }
    }
    for (const obj of draftNode.materialInputSettings) {
      const cid = generateNewMaterialInputCid();
      obj.materialInputVersion.clonedFromId = obj.materialInputVersion.id;
      obj.materialInputVersion.id = null;
      obj.materialInputVersion.cid = cid;
      // Resetting the schema ID to ensure a new schema gets made to avoid reuse especially in new flowcharts
      obj.materialInputVersion.procedureMaterialInputId = null;

      for (const [i, value] of obj.values.entries()) {
        value.id = null;
        value.cid = `${cid}-planned-value-${i}`;
      }
    }
    for (const obj of draftNode.materialOutputSettings) {
      const cid = generateNewMaterialOutputCid();
      obj.materialOutputVersion.clonedFromId = obj.materialOutputVersion.id;
      obj.materialOutputVersion.id = null;
      obj.materialOutputVersion.cid = cid;
      // Resetting the schema ID to ensure a new schema gets made to avoid reuse especially in new flowcharts
      obj.materialOutputVersion.procedureMaterialOutputId = null;
    }
    for (const obj of draftNode.equipmentSettings) {
      const cid = generateNewEquipmentCid();
      obj.equipmentVersion.clonedFromId = obj.equipmentVersion.id;
      obj.equipmentVersion.id = null;
      obj.equipmentVersion.cid = cid;
      // Resetting the schema ID to ensure a new schema gets made to avoid reuse especially in new flowcharts
      obj.equipmentVersion.procedureEquipmentId = null;
      for (const [i, value] of obj.values.entries()) {
        value.id = null;
        value.cid = `${cid}-planned-value-${i}`;
      }
    }
    for (const obj of draftNode.stepGroups) {
      const newSteps = [];
      for (const step of obj.steps) {
        const newStep = duplicateStep(step, step.name);
        assert(step.fromId != null, 'Unexpectedly missing document version id for step');

        newSteps.push(newStep);
        cloneStepCidToDocumentVersionIdMap[newStep.cid] = step.fromId;
      }
      obj.steps = newSteps;
    }

    if (removeTestDefinitionsAndDilutionPlan) {
      draftNode.testDefinitions = [];
      draftNode.dilutionPlan = {
        allowedDilutionFactors: [],
        dilutionAssignment: DilutionAssignmentType.SINGLE_POINT,
      };
    }
  });
  return {newMethodNode, cloneStepCidToDocumentVersionIdMap};
}

/**
 * Replace all necessary UUIDs within flowchart nodes + edges in preparation for procedure duplication
 *
 * Note, we can reuse the existing method CIDs as long as we set the method ID to null - this will trigger
 * new MethodDefinitionVersion creation, and the CID will get switched out with the new method ID.
 * However, we do need to replace material edge handle IDs since we must also regenerate method component
 * UUIDs to ensure method components between the two flowcharts are unique.
 */
export function duplicateFlowchart(flowchart: ProcedureFlowchart): {
  newFlowchart: ProcedureFlowchart;
  cloneStepCidToDocumentVersionIdMap: Record<string, string>;
} {
  const cloneStepCidToDocumentVersionIdMap: Record<string, string> = {};
  const newFlowchart = produce(flowchart, (draftFlowchart) => {
    const materialInputOldToNewCidMapping: {[oldCid: string]: string} = {};
    const materialOutputOldToNewCidMapping: {[oldCid: string]: string} = {};
    draftFlowchart.nodes = draftFlowchart.nodes.map((method) => {
      const {newMethodNode: draftNode, cloneStepCidToDocumentVersionIdMap: stepCidMapForNode} = duplicateNode(
        method,
        generateNewMethodCid(),
        false
      );
      for (const stepCid of Object.keys(stepCidMapForNode)) {
        cloneStepCidToDocumentVersionIdMap[stepCid] = stepCidMapForNode[stepCid];
      }

      draftNode.cid = method.cid;
      draftNode.sourceMethodId = method.id; // Link back to method we cloning
      draftNode.nodeId = method.nodeId; // Preserved so the same method has the same nodeId in the new draft
      draftNode.provenanceId = method.provenanceId;

      draftNode.materialInputSettings.forEach((draftMaterialInputSetting, index) => {
        const materialInputSetting = method.materialInputSettings[index];
        materialInputOldToNewCidMapping[materialInputSetting.materialInputVersion.cid] =
          draftMaterialInputSetting.materialInputVersion.cid;
      });
      draftNode.materialOutputSettings.forEach((draftMaterialOutputSetting, index) => {
        const materialOutputSetting = method.materialOutputSettings[index];
        materialOutputOldToNewCidMapping[materialOutputSetting.materialOutputVersion.cid] =
          draftMaterialOutputSetting.materialOutputVersion.cid;
      });

      return draftNode;
    });

    draftFlowchart.edges = draftFlowchart.edges.map((edge) => {
      if (edge.type === ProcedureEdgeType.METHOD_EDGE) {
        return edge;
      }
      assert(edge.type === ProcedureEdgeType.MATERIAL_EDGE, 'Unexpected edge type supplied');
      return produce(edge, (draftEdge) => {
        const sourceMaterialCid = getMaterialCidFromHandle(draftEdge.sourceHandle);
        const newSourceCid = materialOutputOldToNewCidMapping[sourceMaterialCid];
        draftEdge.sourceHandle = createHandleIdForMaterial(draftEdge.fromMethodCid, newSourceCid, 'source');

        const targetMaterialCid = getMaterialCidFromHandle(draftEdge.targetHandle);
        const newTargetCid = materialInputOldToNewCidMapping[targetMaterialCid];
        draftEdge.targetHandle = createHandleIdForMaterial(draftEdge.toMethodCid, newTargetCid, 'target');
      });
    });
  });
  return {newFlowchart, cloneStepCidToDocumentVersionIdMap};
}

export function getNextLinearMethodNode(
  flowchart: ProcedureFlowchart,
  nodeCid: string
): Optional<MethodNode> {
  const outgoingMethodEdges = flowchart.edges.filter(
    ({type, fromMethodCid}) => type === ProcedureEdgeType.METHOD_EDGE && fromMethodCid === nodeCid
  );
  assert(outgoingMethodEdges.length <= 1, 'Only linear flowcharts are supported');
  const [outgoingEdge] = outgoingMethodEdges;
  return outgoingEdge != null ? flowchart.nodes.find(({cid}) => cid === outgoingEdge.toMethodCid) : null;
}

export function getOrderedDownstreamLinearMethodNodes(
  flowchart: ProcedureFlowchart,
  nodeCid: string
): Array<MethodNode> {
  let nextNode: Optional<MethodNode> = getNextLinearMethodNode(flowchart, nodeCid);
  const downstreamNodes = [];
  while (nextNode != null) {
    downstreamNodes.push(nextNode);
    nextNode = getNextLinearMethodNode(flowchart, nextNode.cid);
  }
  return downstreamNodes;
}

export function emptyDilutionPlan(): DilutionPlan {
  return {allowedDilutionFactors: [], dilutionAssignment: DilutionAssignmentType.SINGLE_POINT};
}

export function makeNewEmptyMethodNode(
  procedureType: ProcedureType,
  gridRow: number,
  gridCol: number,
  newNodeCid: string
): MethodNode {
  const NEW_EMPTY_METHOD: Method = {
    id: null,
    cid: 'new method',
    name: '',
    description: '',
    stepGroups: makeEmptyStepGroups(procedureType),
    dataParameterSettings: [],
    materialInputSettings: [],
    equipmentSettings: [],
    materialOutputSettings: [],
    parameterUsages: {},
    sourceMethodId: null,
    nodeId: uuidv4(),
    provenanceId: uuidv4(),
    analysisTemplateConfigurations: [],
    testDefinitions: [],
    dilutionPlan: emptyDilutionPlan(),
  };

  const {newMethodNode} = duplicateNode(
    {
      ...NEW_EMPTY_METHOD,
      type: ProcedureNodeType.METHOD,
      canvasGridCoordinates: {row: gridRow, column: gridCol},
    },
    newNodeCid,
    true
  );
  return newMethodNode;
}

export function convertMethodToMethodNode(
  method: Method,
  canvasGridCoordinates?: CanvasGridCoordinates
): MethodNode {
  return {
    ...method,
    type: ProcedureNodeType.METHOD,
    canvasGridCoordinates: canvasGridCoordinates ?? {row: 0, column: 0},
  };
}

export function generateNewMethodEdge(sourceMethodCid: string, targetMethodCid: string): MethodEdge {
  const sourceHandle = createHandleIdForMethod(sourceMethodCid, 'source');
  const targetHandle = createHandleIdForMethod(targetMethodCid, 'target');
  return {
    id: createEdgeId(sourceHandle, targetHandle),
    fromMethodCid: sourceMethodCid,
    toMethodCid: targetMethodCid,
    type: ProcedureEdgeType.METHOD_EDGE,
    sourceHandle,
    targetHandle,
  };
}

function generateNewMaterialEdge(edgeInfo: {
  fromMethodCid: string;
  materialOutputCid: string;
  toMethodCid: string;
  materialInputCid: string;
}): MaterialEdge {
  const sourceHandle = createHandleIdForMaterial(
    edgeInfo.fromMethodCid,
    edgeInfo.materialOutputCid,
    'source'
  );
  const targetHandle = createHandleIdForMaterial(edgeInfo.toMethodCid, edgeInfo.materialInputCid, 'target');

  return {
    id: createEdgeId(sourceHandle, targetHandle),
    fromMethodCid: edgeInfo.fromMethodCid,
    toMethodCid: edgeInfo.toMethodCid,
    sourceHandle,
    targetHandle,
    type: ProcedureEdgeType.MATERIAL_EDGE,
  };
}

export function getMaterialAndMethodNameFromCid(
  methodNodes: Array<MethodNode>,
  materialCid: string
): {methodName: string; materialName: string} {
  for (const node of methodNodes) {
    for (const materialInputSetting of node.materialInputSettings) {
      if (materialCid === getMethodComponentCid(materialInputSetting)) {
        return {methodName: node.name, materialName: getMethodComponentName(materialInputSetting)};
      }
    }
    for (const materialOutputSetting of node.materialOutputSettings) {
      if (materialCid === getMethodComponentCid(materialOutputSetting)) {
        return {methodName: node.name, materialName: getMethodComponentName(materialOutputSetting)};
      }
    }
  }
  return {methodName: '', materialName: ''};
}

export function getMaterialFlows(flowchart: ProcedureFlowchart): Array<MaterialFlow> {
  const materialFlows: Array<MaterialFlow> = [];
  for (const edge of flowchart.edges) {
    if (edge.type === ProcedureEdgeType.MATERIAL_EDGE) {
      const sourceMaterialCid = getMaterialCidFromHandle(edge.sourceHandle);
      const targetMaterialCid = getMaterialCidFromHandle(edge.targetHandle);
      const {methodName: sourceMethodName, materialName: sourceMaterialName} =
        getMaterialAndMethodNameFromCid(flowchart.nodes, sourceMaterialCid);
      const {methodName: targetMethodName, materialName: targetMaterialName} =
        getMaterialAndMethodNameFromCid(flowchart.nodes, targetMaterialCid);

      materialFlows.push({
        sourceMaterialCid,
        sourceMaterialName,
        sourceMethodName,
        targetMaterialCid,
        targetMaterialName,
        targetMethodName,
      });
    }
  }
  return materialFlows;
}

export enum AppendDirection {
  UPSTREAM,
  DOWNSTREAM,
}

function updateMethodToAddWithNewCoordinates(
  methodToAdd: MethodNode,
  appendTargetNode: MethodNode,
  appendDirection: AppendDirection
): MethodNode {
  const columnAdjust = appendDirection === AppendDirection.DOWNSTREAM ? 1 : -1;
  return produce(methodToAdd, (draftNode) => {
    draftNode.canvasGridCoordinates = {
      row: appendTargetNode.canvasGridCoordinates.row,
      column: appendTargetNode.canvasGridCoordinates.column + columnAdjust,
    };
  });
}

function rightShiftAllDownstreamCanvasCoordinatesAfterNode(
  flowchart: ProcedureFlowchart,
  nodeToShiftAfter: MethodNode
): ProcedureFlowchart {
  return produce(flowchart, (draftFlowchart) => {
    const downstreamLinearMethodNodes = getOrderedDownstreamLinearMethodNodes(
      draftFlowchart,
      nodeToShiftAfter.cid
    );
    downstreamLinearMethodNodes.forEach((downstreamNode) => {
      downstreamNode.canvasGridCoordinates.column += 1; // Right shift nodes after insertion point
    });
  });
}

interface AddMethodSettingsWithNode {
  appendDirection?: AppendDirection;
  methodToAdd: MethodNode;
}

interface AddMethodSettingsWithoutNode {
  appendDirection?: AppendDirection;
  methodToAdd?: never;
  newMethodCid: string;
}

type AddMethodSettings = AddMethodSettingsWithNode | AddMethodSettingsWithoutNode;

interface AddMethodOutcome {
  updatedFlowchart: ProcedureFlowchart;
  newMethodCid: string;
}

/**
 * Add a method before/after a source method in a linear flowchart. If no method to add is supplied,
 * will create a new empty method.
 *
 * If the source method is a root, will prepend the new method before the source method.
 * If the source method is a leaf, will simply append a new method afterwards.
 * Otherwise, we split the outgoing edge from the source method and insert a new node.
 */
export function addMethodToLinearFlowchart(
  procedureType: ProcedureType,
  flowchart: ProcedureFlowchart,
  sourceMethodCid: string,
  addMethodSettings: AddMethodSettings
): AddMethodOutcome {
  const outgoingEdges = flowchart.edges.filter(
    (edge) => edge.type === ProcedureEdgeType.METHOD_EDGE && edge.fromMethodCid === sourceMethodCid
  );
  assert(outgoingEdges.length <= 1, 'Unexpectedly found a non-linear flowchart');
  const incomingEdges = flowchart.edges.filter(
    (edge) => edge.type === ProcedureEdgeType.METHOD_EDGE && edge.toMethodCid === sourceMethodCid
  );
  assert(incomingEdges.length <= 1, 'Unexpectedly found a non-linear flowchart');

  const appendTargetNode = flowchart.nodes.find((node) => node.cid === sourceMethodCid);
  assert(appendTargetNode != null, 'Unexpectedly could not find node', {value: sourceMethodCid});
  const newMethodToAdd =
    addMethodSettings.methodToAdd ??
    makeNewEmptyMethodNode(
      procedureType,
      appendTargetNode.canvasGridCoordinates.row,
      appendTargetNode.canvasGridCoordinates.column,
      addMethodSettings.newMethodCid
    );
  const {appendDirection = AppendDirection.DOWNSTREAM} = addMethodSettings;
  const newNode = updateMethodToAddWithNewCoordinates(newMethodToAdd, appendTargetNode, appendDirection);

  let updatedFlowchart = flowchart;
  if (appendDirection === AppendDirection.UPSTREAM && incomingEdges.length === 0) {
    updatedFlowchart = prependFromRootNode(newNode, sourceMethodCid, flowchart);
    return {updatedFlowchart, newMethodCid: newNode.cid};
  }
  if (outgoingEdges.length === 0) {
    updatedFlowchart = appendToLeafNode(sourceMethodCid, newNode, flowchart);
  } else {
    updatedFlowchart = insertBetweenTwoNodes(outgoingEdges, flowchart, newNode);
  }
  return {updatedFlowchart, newMethodCid: newNode.cid};
}

function prependFromRootNode(
  newNode: MethodNode,
  sourceMethodCid: string,
  flowchart: ProcedureFlowchart
): ProcedureFlowchart {
  const newEdge: MethodEdge = generateNewMethodEdge(newNode.cid, sourceMethodCid);
  return produce(flowchart, (draftFlowchart) => {
    draftFlowchart.edges = draftFlowchart.edges.concat([newEdge]);
    draftFlowchart.nodes.push(newNode);
    draftFlowchart.nodes = rightShiftAllDownstreamCanvasCoordinatesAfterNode(draftFlowchart, newNode).nodes;
  });
}

function appendToLeafNode(
  sourceMethodCid: string,
  newNode: MethodNode,
  flowchart: ProcedureFlowchart
): ProcedureFlowchart {
  const newEdge: MethodEdge = generateNewMethodEdge(sourceMethodCid, newNode.cid);
  return {edges: [...flowchart.edges, newEdge], nodes: [...flowchart.nodes, newNode]};
}

function insertBetweenTwoNodes(
  outgoingEdges: Array<ProcedureEdge>,
  flowchart: ProcedureFlowchart,
  newNode: MethodNode
): ProcedureFlowchart {
  const edgeToSplit = outgoingEdges[0];
  const {fromMethodCid, toMethodCid} = edgeToSplit;
  const fromMethod = flowchart.nodes.find((node) => node.cid === fromMethodCid);
  assert(fromMethod != null, 'Unexpectedly could not find method', {value: fromMethodCid});

  const newSourceEdge = generateNewMethodEdge(fromMethodCid, newNode.cid);
  const newTargetEdge = generateNewMethodEdge(newNode.cid, toMethodCid);
  return produce(flowchart, (draftFlowchart) => {
    draftFlowchart.edges = draftFlowchart.edges
      .filter((e) => e.id !== edgeToSplit.id)
      .concat([newSourceEdge, newTargetEdge]);
    draftFlowchart.nodes.push(newNode);
    draftFlowchart.nodes = rightShiftAllDownstreamCanvasCoordinatesAfterNode(draftFlowchart, newNode).nodes;
  });
}

/**
 * Duplicate method and insert after the source method. Assumes a linear flowchart.
 */
export function duplicateMethodFromLinearFlowchart(
  flowchart: ProcedureFlowchart,
  methodCidToDuplicate: string,
  newNodeCid: string,
  procedureType: ProcedureType
): {
  newFlowchart: ProcedureFlowchart;
  newMethodCid: string;
  cloneStepCidToDocumentVersionIdMap: Record<string, string>;
} {
  const methodToDuplicate = flowchart.nodes.find((node) => node.cid === methodCidToDuplicate);
  assert(methodCidToDuplicate != null, 'Did not find a method to duplicate with the given method Id');
  if (methodToDuplicate?.type === ProcedureNodeType.METHOD) {
    const {newMethodNode, cloneStepCidToDocumentVersionIdMap} = duplicateNode(
      methodToDuplicate,
      newNodeCid,
      true,
      normalizeNameForUniqueness(
        methodToDuplicate.name,
        flowchart.nodes.map((node) => node.name),
        getMethodNamePlaceholder(procedureType)
      )
    );
    const {updatedFlowchart} = addMethodToLinearFlowchart(procedureType, flowchart, methodCidToDuplicate, {
      methodToAdd: newMethodNode,
    });
    return {
      newFlowchart: updatedFlowchart,
      newMethodCid: newMethodNode.cid,
      cloneStepCidToDocumentVersionIdMap,
    };
  }
  assert(false, 'Unsupported procedure node type');
}

function getReplacementMaterialEdgesForMaterialName(
  incomingMaterialEdges: Array<MaterialEdge>,
  outgoingMaterialEdges: Array<MaterialEdge>
): Array<MaterialEdge> {
  return incomingMaterialEdges.flatMap((inEdge) =>
    outgoingMaterialEdges.map((outEdge) => {
      const materialOutCid = getMaterialCidFromHandle(inEdge.sourceHandle);
      const materialInCid = getMaterialCidFromHandle(outEdge.targetHandle);
      return generateNewMaterialEdge({
        fromMethodCid: inEdge.fromMethodCid,
        materialOutputCid: materialOutCid,
        toMethodCid: outEdge.toMethodCid,
        materialInputCid: materialInCid,
      });
    })
  );
}

export function reorderMethodsInLinearFlowchart(
  procedureType: ProcedureType,
  flowchart: ProcedureFlowchart,
  methodCidToReorder: string,
  dropZone: MethodDropZone
): ProcedureFlowchart {
  const methodToReorder = flowchart.nodes.find((node) => node.cid === methodCidToReorder);
  assert(methodToReorder != null, 'Method to reorder must exist in flowchart');

  let upstreamTargetMethodCid;
  let downstreamTargetMethodCid;
  let appendDirection = AppendDirection.DOWNSTREAM;
  switch (dropZone.type) {
    case DropZoneType.METHOD_ROOT:
      upstreamTargetMethodCid = dropZone.prependToMethodCid;
      appendDirection = AppendDirection.UPSTREAM;
      break;
    case DropZoneType.METHOD_LEAF:
      upstreamTargetMethodCid = dropZone.appendFromMethodCid;
      break;
    case DropZoneType.METHOD_EDGE:
      upstreamTargetMethodCid = dropZone.fromMethod;
      downstreamTargetMethodCid = dropZone.toMethod;
      break;
    default:
      assert(false, 'Unsupported drop zone type supplied');
  }

  if (methodCidToReorder === upstreamTargetMethodCid || methodCidToReorder === downstreamTargetMethodCid) {
    return flowchart;
  }

  const cachedMaterialEdges = flowchart.edges.filter((edge) => edge.type === ProcedureEdgeType.MATERIAL_EDGE);

  // Intermediate deletion step - methodToReorder and material edges will retain all properties after reorder
  // Note, material edges may become invalid after this reorder - will be flagged with the validation system
  let updatedFlowchart = deleteMethodFromLinearFlowchart(flowchart, methodCidToReorder);
  updatedFlowchart = addMethodToLinearFlowchart(procedureType, updatedFlowchart, upstreamTargetMethodCid, {
    methodToAdd: methodToReorder,
    appendDirection,
  }).updatedFlowchart;

  const updatedEdges: Array<ProcedureEdge> = [
    ...updatedFlowchart.edges.filter((edge) => edge.type !== ProcedureEdgeType.MATERIAL_EDGE),
    ...cachedMaterialEdges,
  ];
  return {...updatedFlowchart, edges: updatedEdges};
}

function getUniqueMaterialEdges(materialEdge: Array<MaterialEdge>): Array<MaterialEdge> {
  return Object.values(
    mapObject(
      groupBy(materialEdge, (edge: MaterialEdge) => edge.id),
      (value) => value[0]
    )
  );
}

function buildMaterialEdgesForCommonMaterials(
  incomingMaterialEdgesByMaterialName: {[materialId: string]: Array<MaterialEdge>},
  outgoingMaterialEdgesByMaterialName: {[materialId: string]: Array<MaterialEdge>}
): Array<MaterialEdge> {
  const namesOfIncomingMaterials = Object.keys(incomingMaterialEdgesByMaterialName);
  const namesOfOutgoingMaterials = Object.keys(outgoingMaterialEdgesByMaterialName);
  const linkedMaterialNamesIO = intersect([namesOfIncomingMaterials, namesOfOutgoingMaterials]);

  // Build a cartesian product of material edges for each linked material name
  const newReplacementMergedMaterialEdges = linkedMaterialNamesIO.flatMap((materialName: string) => {
    const incomingEdges = incomingMaterialEdgesByMaterialName[materialName] ?? [];
    const outgoingEdges = outgoingMaterialEdgesByMaterialName[materialName] ?? [];
    return getReplacementMaterialEdgesForMaterialName(incomingEdges, outgoingEdges);
  });

  return getUniqueMaterialEdges(newReplacementMergedMaterialEdges);
}

export function getMergedMaterialEdgesFromInternalNames(
  deletedMethod: Method,
  materialEdgesToDelete: Array<MaterialEdge>
): Array<MaterialEdge> {
  const incomingMaterialEdges = materialEdgesToDelete.filter(
    ({toMethodCid}) => toMethodCid === deletedMethod.cid
  );
  const outgoingMaterialEdges = materialEdgesToDelete.filter(
    ({fromMethodCid}) => fromMethodCid === deletedMethod.cid
  );

  const materialInputNameByMaterialId: {[materialId: string]: string} = mapObject(
    groupByUniqueKey(deletedMethod.materialInputSettings, (mis) => getMethodComponentCid(mis)),
    (mis) => mis.materialInputVersion.name
  );
  const materialOutputNameByMaterialId: {[materialId: string]: string} = mapObject(
    groupByUniqueKey(deletedMethod.materialOutputSettings, (mos) => getMethodComponentCid(mos)),
    (mos) => mos.materialOutputVersion.name
  );
  const materialNameByMaterialId: {[materialId: string]: string} = Object.assign(
    {},
    materialInputNameByMaterialId,
    materialOutputNameByMaterialId
  );

  const incomingEdgesByInternalMaterialName = groupBy(
    incomingMaterialEdges,
    (edge) => materialNameByMaterialId[getMaterialCidFromHandle(edge.targetHandle)]
  );

  const outgoingEdgesByInternalMaterialName = groupBy(
    outgoingMaterialEdges,
    (edge) => materialNameByMaterialId[getMaterialCidFromHandle(edge.sourceHandle)]
  );

  return buildMaterialEdgesForCommonMaterials(
    incomingEdgesByInternalMaterialName,
    outgoingEdgesByInternalMaterialName
  );
}

export function getMergedMaterialEdgesFromExternalNames(
  flowchart: ProcedureFlowchart,
  deletedMethod: Method,
  materialEdgesToDelete: Array<MaterialEdge>
): Array<MaterialEdge> {
  const incomingMaterialEdges = materialEdgesToDelete.filter(
    ({toMethodCid}) => toMethodCid === deletedMethod.cid
  );
  const outgoingMaterialEdges = materialEdgesToDelete.filter(
    ({fromMethodCid}) => fromMethodCid === deletedMethod.cid
  );
  const materialEdgesByExternalMethodCid = groupBy(materialEdgesToDelete, (edge) =>
    edge.fromMethodCid === deletedMethod.cid ? edge.toMethodCid : edge.fromMethodCid
  );
  const materialsWithExistingConnection = new Set(
    materialEdgesToDelete.map((edge) =>
      edge.fromMethodCid === deletedMethod.cid
        ? getMaterialCidFromHandle(edge.targetHandle)
        : getMaterialCidFromHandle(edge.sourceHandle)
    )
  );

  const externalMethodCids = Object.keys(materialEdgesByExternalMethodCid).flat();
  const materialNameByMaterialId: {[materialId: string]: string} = externalMethodCids
    .map((methodCid) => {
      const node = flowchart.nodes.find((node) => node.cid === methodCid);
      assert(node != null, 'Node is expected for given method Id');
      // We keep input and output objects separate for filtering so that we
      // can invoke the correct functions based on type.
      const materialInputSettings = node.materialInputSettings.filter((mis) =>
        materialsWithExistingConnection.has(getMethodComponentCid(mis))
      );
      const materialOutputSettings = node.materialOutputSettings.filter((mos) =>
        materialsWithExistingConnection.has(getMethodComponentCid(mos))
      );
      // We keep input and output objects separate during the mapping as well
      // so that we can perform the correct conversions. We can then combine
      // the two once the type-specific information has been extracted.
      return Object.assign(
        {},
        mapObject(
          groupByUniqueKey(materialInputSettings, (mis) => getMethodComponentCid(mis)),
          (mis) => mis.materialInputVersion.name
        ),
        mapObject(
          groupByUniqueKey(materialOutputSettings, (mos) => getMethodComponentCid(mos)),
          (mos) => mos.materialOutputVersion.name
        )
      );
    })
    // We combine the inputs and outputs from each of the methods
    // together into a final mapping.
    .reduce((acc, mapping) => Object.assign({}, acc, mapping), {});

  const incomingEdgesByExternalMaterialName = groupBy(
    incomingMaterialEdges,
    (edge) => materialNameByMaterialId[getMaterialCidFromHandle(edge.sourceHandle)]
  );

  const outgoingEdgesByExternalMaterialName = groupBy(
    outgoingMaterialEdges,
    (edge) => materialNameByMaterialId[getMaterialCidFromHandle(edge.targetHandle)]
  );

  return buildMaterialEdgesForCommonMaterials(
    incomingEdgesByExternalMaterialName,
    outgoingEdgesByExternalMaterialName
  );
}

/**
 *  Gets the self-healed material edges when a method is deleted.
 *
 */
export function getSelfHealedMaterialEdges(
  flowchart: ProcedureFlowchart,
  deletedMethod: Method,
  materialEdgesToDelete: Array<MaterialEdge>
): Array<MaterialEdge> {
  if (materialEdgesToDelete.length === 0) {
    return materialEdgesToDelete;
  }

  return getUniqueMaterialEdges([
    ...getMergedMaterialEdgesFromExternalNames(flowchart, deletedMethod, materialEdgesToDelete),
    ...getMergedMaterialEdgesFromInternalNames(deletedMethod, materialEdgesToDelete),
  ]);
}

/**
 * Delete method and make new edges to keep adjacent methods connected. Assumes a linear flowchart.
 */
export function deleteMethodFromLinearFlowchart(
  flowchart: ProcedureFlowchart,
  methodCid: string
): ProcedureFlowchart {
  const methodEdges = flowchart.edges.filter((edge) => edge.type === ProcedureEdgeType.METHOD_EDGE);
  const methodNodes = flowchart.nodes.filter((node) => node.cid === methodCid);
  assert(methodNodes.length === 1, 'Did not find exactly one method with expected id');
  const methodNode = methodNodes[0];
  const incomingMethodEdges = methodEdges.filter((edge) => edge.toMethodCid === methodCid);
  assert(incomingMethodEdges.length <= 1, 'Expecting flowchart to be linear');
  const incomingMethodEdgeToDelete = incomingMethodEdges[0];
  const outgoingMethodEdges = methodEdges.filter((edge) => edge.fromMethodCid === methodCid);
  assert(outgoingMethodEdges.length <= 1, 'Expecting flowchart to be linear');
  const outgoingMethodEdgeToDelete = outgoingMethodEdges[0];
  const materialEdgesToDelete = flowchart.edges.filter(
    (edge): edge is MaterialEdge =>
      edge.type === ProcedureEdgeType.MATERIAL_EDGE &&
      (edge.toMethodCid === methodCid || edge.fromMethodCid === methodCid)
  );
  const materialEdgeIdsToDelete = materialEdgesToDelete.map((edge) => edge.id);
  const remainingEdges = flowchart.edges.filter(
    (edge) =>
      edge.id !== outgoingMethodEdgeToDelete?.id &&
      edge.id !== incomingMethodEdgeToDelete?.id &&
      !materialEdgeIdsToDelete.includes(edge.id)
  );

  const newReplacementMethodEdgesAfterDeletion =
    incomingMethodEdgeToDelete != null && outgoingMethodEdgeToDelete != null
      ? [
          generateNewMethodEdge(
            incomingMethodEdgeToDelete.fromMethodCid,
            outgoingMethodEdgeToDelete.toMethodCid
          ),
        ]
      : [];

  const finalEdges = remainingEdges.concat(
    getSelfHealedMaterialEdges(flowchart, methodNode, materialEdgesToDelete),
    newReplacementMethodEdgesAfterDeletion
  );

  const updatedNodes = flowchart.nodes.filter((node) => node.cid !== methodCid);
  return {edges: finalEdges, nodes: updatedNodes};
}

export function changeMethodName(
  flowchart: ProcedureFlowchart,
  methodCid: string,
  newMethodName: string,
  procedureType: ProcedureType
): ProcedureFlowchart {
  const allMethodNamesExcludingCurrentMethod = flowchart.nodes
    .filter((node) => node.cid !== methodCid)
    .map((node) => node.name);

  const newNodes = flowchart.nodes.map((node) =>
    node.cid !== methodCid
      ? node
      : {
          ...node,
          name: normalizeNameForUniqueness(
            newMethodName,
            allMethodNamesExcludingCurrentMethod,
            getMethodNamePlaceholder(procedureType)
          ),
        }
  );
  return {edges: flowchart.edges, nodes: newNodes};
}

function nameFormatForDuplicates(name: string, increment: number): string {
  const suffix = ` (${increment})`;
  const maxBaseNameLength = MAX_ENTRY_NAME_LENGTH - suffix.length;
  const truncatedBaseName = name.slice(0, maxBaseNameLength);
  return `${truncatedBaseName}${suffix}`;
}

export function normalizeNameForUniqueness(
  name: string,
  namesAlreadyInUse: Array<string>,
  emptyNamePlaceholder: string
): string {
  const trimmedName = name.trim().slice(0, MAX_ENTRY_NAME_LENGTH);
  const unavailableNames = new Set([...namesAlreadyInUse, emptyNamePlaceholder]);

  if (trimmedName && !unavailableNames.has(trimmedName)) {
    return trimmedName;
  }

  const baseName = trimmedName || emptyNamePlaceholder;
  let increment = 1;
  let newName = nameFormatForDuplicates(baseName, increment);
  while (unavailableNames.has(newName)) {
    newName = nameFormatForDuplicates(baseName, ++increment);
  }
  return newName;
}

export function changeMethodDetails(flowchart: ProcedureFlowchart, methodData: Method): ProcedureFlowchart {
  return produce(flowchart, (draftFlowchart) => {
    const modifiedNode = draftFlowchart.nodes.find(({cid}) => cid === methodData.cid);
    assert(modifiedNode != null, 'Could not find a node with the id');

    // Remove all material edges that have a deleted input or output handle
    const updatedMaterialInputIds = methodData.materialInputSettings.map((input) =>
      getMethodComponentCid(input)
    );
    const removedMaterialInputHandles = modifiedNode.materialInputSettings
      .filter((input) => !updatedMaterialInputIds.includes(getMethodComponentCid(input)))
      .map((deletedInput) =>
        createHandleIdForMaterial(modifiedNode.cid, getMethodComponentCid(deletedInput), 'target')
      );

    const updatedMaterialOutputIds = methodData.materialOutputSettings.map((output) =>
      getMethodComponentCid(output)
    );
    const removedMaterialOutputHandles = modifiedNode.materialOutputSettings
      .filter((output) => !updatedMaterialOutputIds.includes(getMethodComponentCid(output)))
      .map((deletedOutput) =>
        createHandleIdForMaterial(modifiedNode.cid, getMethodComponentCid(deletedOutput), 'source')
      );

    Object.assign(modifiedNode, methodData);
    draftFlowchart.edges = draftFlowchart.edges.filter(
      (edge) =>
        !(
          edge.type === ProcedureEdgeType.MATERIAL_EDGE &&
          (removedMaterialOutputHandles.includes(edge.sourceHandle) ||
            removedMaterialInputHandles.includes(edge.targetHandle))
        )
    );
  });
}

export function deleteMaterialEdge(flowchart: ProcedureFlowchart, edgeId: string): ProcedureFlowchart {
  return {
    edges: flowchart.edges.filter((edge) =>
      edge.type === ProcedureEdgeType.MATERIAL_EDGE ? edge.id !== edgeId : true
    ),
    nodes: flowchart.nodes,
  };
}

export function getNewProcedureFlowchart(procedureType: ProcedureType): ProcedureFlowchart {
  const initialNode = makeNewEmptyMethodNode(procedureType, 40, 200, generateNewMethodCid());
  return {
    nodes: [initialNode],
    edges: [],
  };
}

/**
 * Determine if a material edge is going from an earlier to later method in the flowchart
 * (ie, material outputs must be created in a previous method in order to be an input to another method)
 */
export function isMaterialEdgeFlowingDownstream(
  sourceMethodCid: string,
  targetMethodCid: string,
  methodEdges: Array<MethodEdge>
): boolean {
  const downstreamEdgeAdjMatrix: Record<string, Array<string>> = mapObject(
    groupBy(methodEdges, (edge) => edge.fromMethodCid),
    (edges) => edges.map((edge) => edge.toMethodCid)
  );

  return depthFirstEdgeSearch(downstreamEdgeAdjMatrix, sourceMethodCid, targetMethodCid);
}

/**
 * Depth first search of downstream edge adjacency matrix from a source method to a target method
 */
function depthFirstEdgeSearch(
  adjacencyMatrix: Record<string, Array<string>>,
  sourceMethodCid: string,
  targetMethodCid: string
): boolean {
  const visited = new Set<string>();
  const stack = [];
  stack.push(sourceMethodCid);

  while (stack.length !== 0) {
    const currentMethodCid = stack.pop();
    assert(currentMethodCid != null, 'Current method id was null');
    visited.add(currentMethodCid);
    const nextToVisit = adjacencyMatrix[currentMethodCid] ?? [];
    if (nextToVisit.includes(targetMethodCid)) {
      return true;
    }

    nextToVisit.forEach((nextMethodCid) => {
      if (!visited.has(nextMethodCid)) {
        stack.push(nextMethodCid);
      }
    });
  }
  return false;
}

export function extractMethodNodes(nodes: Array<MethodNode>): Array<MethodNode> {
  return nodes.filter((node): node is MethodNode => node.type === ProcedureNodeType.METHOD);
}

export function extractMethodEdges(edges: Array<ProcedureEdge>): Array<MethodEdge> {
  return edges.filter((edge): edge is MethodEdge => edge.type === ProcedureEdgeType.METHOD_EDGE);
}

export function extractMaterialEdges(edges: Array<ProcedureEdge>): Array<MaterialEdge> {
  return edges.filter((edge): edge is MaterialEdge => edge.type === ProcedureEdgeType.MATERIAL_EDGE);
}

export function sortNodesByCoordinates<
  TNode extends {[k in TKey]: CanvasGridCoordinates},
  TKey extends string,
>(methodNodes: Array<TNode>, key: TKey): Array<TNode> {
  return methodNodes.sort((a, b) => {
    const rowComparison = a[key].row - b[key].row;
    const colComparison = a[key].column - b[key].column;
    return rowComparison || colComparison;
  });
}

export function sortMethodNodesByRowColumn<T extends MethodNode>(methodNodes: Array<T>): Array<T> {
  return sortNodesByCoordinates(methodNodes, 'canvasGridCoordinates');
}

/**
 * Use the height of the tallest node and the number of nodes in the flowchart to determine the starting
 * position to render the flowchart. The flowchart is horizontally left-aligned and vertically center-aligned.
 * First, an absolute height and width is calculated and then a zoom value is applied if the flowchart is too
 * tall or wide. The maximum zoom applied is 1 and the minimum is 0.75.
 *
 * @param flowchart
 * @param containerWidth
 * @param containerHeight
 */
export function getFramedViewport(
  flowchart: ProcedureFlowchart,
  containerWidth: number,
  containerHeight: number
): {x: number; y: number; zoom: number} {
  const adjustedContainerHeight = containerHeight - PROCEDURE_HEADER_HEIGHT;

  const flowchartWidth =
    flowchart.nodes.length * COMMON_NODE_WIDTH +
    (flowchart.nodes.length - 1) * (2 * DASHED_LINE_LENGTH + ADD_NODE_BUTTON_WIDTH) +
    DASHED_LINE_LENGTH * 2 +
    ADD_NODE_BUTTON_WIDTH * 2;

  const nodeHeights = [];
  for (const node of flowchart.nodes) {
    const numberOfSubElements =
      node.materialInputSettings.length +
      node.materialOutputSettings.length +
      node.dataParameterSettings.length +
      node.equipmentSettings.length;

    nodeHeights.push(
      NODE_NAME_BOX_HEIGHT +
        SUB_TITLE_HEIGHT * 4 + // combined height of all node section titles
        numberOfSubElements * SUB_ELEMENT_HEIGHT + // combined height of all values in each section
        1 // added to account for node's bottom border
    );
  }
  const flowchartHeight = Math.max(...nodeHeights);

  // apply zoom to both y and x axis and use the smaller zoom to ensure that flowchart fits in view.
  // Padding is used to prevent flowchart from rendering on the on edge
  const xZoom = (containerWidth - 2 * FLOWCHART_PADDING_X) / flowchartWidth;
  const yZoom = (adjustedContainerHeight - 2 * FLOWCHART_PADDING_Y) / flowchartHeight;

  const zoom = Math.max(
    Math.min(MAX_PROCEDURE_FLOWCHART_DEFAULT_ZOOM, xZoom, yZoom),
    MIN_PROCEDURE_FLOWCHART_DEFAULT_ZOOM
  );

  // These values are absolute pixel values and not relative zoomed canvas values. Thus, any numbers that were
  // affected by the zoom must be reduced. X position of flowchart is left-alligned with some padding.
  // Y position is middle-alligned so the flowchart is shifted down if it's wider than tall.
  return {
    x: (DASHED_LINE_LENGTH + ADD_NODE_BUTTON_WIDTH) * zoom + FLOWCHART_PADDING_X,
    y: (adjustedContainerHeight - flowchartHeight * zoom) / 2,
    zoom,
  };
}
